import { z } from "zod";
import type {
  EnvName,
  UiContext,
  VoiceCommandWire as ApiVoiceCommandWire,
} from "./api-types";

type AddTransientCommand = {
  type: "add_transient";
  callsign: string;
  aircraft_type: string;
  origin: string;
  eta_zulu: string;
  etd_zulu: string;
  destination: string;
  parking_spot: string;
};

type ClearTransientsCommand = {
  type: "clear_transients";
  aircraft_type: string;
  callsign: string;
};

type UpdateTransientCommand = {
  type: "update_transient";
  transient_id: string;
  callsign?: string | null;
  aircraft_type?: string | null;
  origin?: string | null;
  eta_zulu?: string | null;
  etd_zulu?: string | null;
  destination?: string | null;
  parking_spot?: string | null;
};

type SyncWeatherCommand = {
  type: "sync_weather";
  icao: string;
};

export type VoiceCommandWire =
  | ApiVoiceCommandWire
  | AddTransientCommand
  | UpdateTransientCommand
  | ClearTransientsCommand
  | SyncWeatherCommand;
export type UiSnapshot = UiContext;

export type SectionHandle = {
  patch: (patch: {
    answers?: Record<string, string>;
    outlook_48h?: Record<string, string>;
    tier?: string | null;
  }) => void;
  clear: () => void;
  submit: () => void | Promise<void>;
  snapshot: () => UiSnapshot;
};

/** Section codes — keep in sync with ellsworth.types.SectionCode */
export const SECTION_CODES = ["WX", "AMOPS", "TOWER", "RAPCON", "RAWS"] as const;

export type SectionDraftPatch = {
  answers: Record<string, string>;
  outlook_48h: Record<string, string>;
  tier?: string | null;
};

export function sectionCodeFromPath(pathname: string): string | null {
  const match = pathname.match(/\/section\/([^/]+)/i);
  return match?.[1]?.toUpperCase() ?? null;
}

/** Runtime wire validation — defaults mirror PatchCommand in openapi.json */
const navigateCmd = z.object({ type: z.literal("navigate"), target: z.string() });
const patchCmd = z.object({
  type: z.literal("patch"),
  section_code: z.string(),
  answers: z.record(z.string()).default({}),
  outlook_48h: z.record(z.string()).default({}),
  tier: z.string().nullable().optional(),
});
const clearCmd = z.object({ type: z.literal("clear"), section_code: z.string() });
const submitCmd = z.object({ type: z.literal("submit"), section_code: z.string() });
const addTransientCmd = z.object({
  type: z.literal("add_transient"),
  callsign: z.string().default(""),
  aircraft_type: z.string(),
  origin: z.string().default(""),
  eta_zulu: z.string().default(""),
  etd_zulu: z.string().default(""),
  destination: z.string().default(""),
  parking_spot: z.string().default(""),
});
const clearTransientsCmd = z.object({
  type: z.literal("clear_transients"),
  aircraft_type: z.string().default(""),
  callsign: z.string().default(""),
});
const updateTransientCmd = z.object({
  type: z.literal("update_transient"),
  transient_id: z.string().uuid(),
  callsign: z.string().nullable().optional(),
  aircraft_type: z.string().nullable().optional(),
  origin: z.string().nullable().optional(),
  eta_zulu: z.string().nullable().optional(),
  etd_zulu: z.string().nullable().optional(),
  destination: z.string().nullable().optional(),
  parking_spot: z.string().nullable().optional(),
});
const syncWeatherCmd = z.object({
  type: z.literal("sync_weather"),
  icao: z.string().default(""),
});

const voiceCommandWireSchema = z.discriminatedUnion("type", [
  navigateCmd,
  patchCmd,
  clearCmd,
  submitCmd,
  addTransientCmd,
  updateTransientCmd,
  clearTransientsCmd,
  syncWeatherCmd,
]);

export type ParsedVoiceCommands = {
  commands: VoiceCommandWire[];
  rejected: unknown[];
};

export function parseVoiceCommands(raw: unknown): ParsedVoiceCommands {
  if (raw == null) {
    return { commands: [], rejected: [] };
  }
  const items = Array.isArray(raw) ? raw : [raw];
  const commands: VoiceCommandWire[] = [];
  const rejected: unknown[] = [];
  for (const item of items) {
    const parsed = voiceCommandWireSchema.safeParse(item);
    if (parsed.success) {
      commands.push(parsed.data as VoiceCommandWire);
    } else {
      rejected.push(item);
    }
  }
  return { commands, rejected };
}

export function resolveNavigateTarget(env: EnvName, target: string): string {
  const t = target.trim().toUpperCase();
  if (t === "DASHBOARD" || t === "") return `/${env}`;
  if (t === "BRIEF") return `/${env}/brief`;
  if ((SECTION_CODES as readonly string[]).includes(t)) {
    return `/${env}/section/${t}`;
  }
  return `/${env}/section/${target.trim()}`;
}

type DispatchDeps = {
  env: EnvName;
  navigate: (path: string) => void;
  getHandle: (sectionCode: string) => SectionHandle | null;
  queuePending: (cmds: VoiceCommandWire[]) => void;
};

function applyToSection(handle: SectionHandle, cmd: VoiceCommandWire): void | Promise<void> {
  switch (cmd.type) {
    case "patch":
      handle.patch({
        answers: cmd.answers,
        outlook_48h: cmd.outlook_48h,
        tier: cmd.tier,
      });
      return;
    case "clear":
      handle.clear();
      return;
    case "submit":
      return Promise.resolve(handle.submit());
    default:
      return;
  }
}

export function dedupeVoiceCommands(commands: VoiceCommandWire[]): VoiceCommandWire[] {
  const seenSubmit = new Set<string>();
  const out: VoiceCommandWire[] = [];
  for (const cmd of commands) {
    if (cmd.type === "submit") {
      const code = cmd.section_code.trim().toUpperCase();
      if (seenSubmit.has(code)) continue;
      seenSubmit.add(code);
    }
    out.push(cmd);
  }
  return out;
}

export async function applyCommands(commands: VoiceCommandWire[], deps: DispatchDeps) {
  const pending: VoiceCommandWire[] = [];

  for (const cmd of dedupeVoiceCommands(commands)) {
    await matchCommand(cmd, deps, pending);
  }

  if (pending.length > 0) {
    deps.queuePending(pending);
  }
}

async function matchCommand(
  cmd: VoiceCommandWire,
  deps: DispatchDeps,
  pending: VoiceCommandWire[],
): Promise<void> {
  switch (cmd.type) {
    case "navigate": {
      const path = resolveNavigateTarget(deps.env, cmd.target);
      deps.navigate(path);
      break;
    }
    case "add_transient":
    case "update_transient":
    case "clear_transients":
    case "sync_weather":
      break;
    case "patch":
    case "clear":
    case "submit": {
      const code = cmd.section_code.trim().toUpperCase();
      const handle = deps.getHandle(code);
      if (handle) {
        await applyToSection(handle, cmd);
      } else {
        pending.push(cmd);
      }
      break;
    }
  }
}

export async function flushPendingForSection(
  sectionCode: string,
  pending: VoiceCommandWire[],
  getHandle: (sectionCode: string) => SectionHandle | null,
): Promise<VoiceCommandWire[]> {
  const code = sectionCode.trim().toUpperCase();
  const handle = getHandle(code);
  if (!handle) return pending;

  const rest: VoiceCommandWire[] = [];
  for (const cmd of pending) {
    if (cmd.type === "add_transient" || cmd.type === "update_transient") {
      rest.push(cmd);
      continue;
    }
    if (cmd.type === "clear_transients") {
      rest.push(cmd);
      continue;
    }
    if (cmd.type === "sync_weather") {
      rest.push(cmd);
      continue;
    }
    if (cmd.type !== "navigate" && cmd.section_code.trim().toUpperCase() === code) {
      await applyToSection(handle, cmd);
    } else {
      rest.push(cmd);
    }
  }
  return rest;
}
